import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';

export default function CustomerForm() {

    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    const customerFormSchema: DynamicFormSchema = {
        layout: "vertical",
        showResetButton: true,
        sections: [
            {
                key: "basic-info",
                title: "Customer Basic Information",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: "name",
                        label: "Name",
                        type: "input",
                        required: true,
                        pattern: "AlphanumericWithSpaceAndSymbols",
                        validation: [{ max: 255 }],
                        colSpan: 12,
                    },
                    {
                        name: 'isActive',
                        label: 'Status',
                        type: 'switch',
                        colSpan: 12, // Full width
                        defaultValue: true, 
                    },
                ],
            },
            {
                key: "contact-info",
                title: "Contact Information",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: "phoneNo",
                        label: "Phone No",
                        type: "input",
                        pattern: "PhoneNumber",
                        colSpan: 12,
                    },
                    {
                        name: "mobileNo",
                        label: "Mobile No",
                        type: "input",
                        pattern: "MobileNumber",
                        colSpan: 12,
                    },
                    {
                        name: "email",
                        label: "Email",
                        type: "input",
                        pattern: "Email",
                        validation: [{ max: 256 }],
                        colSpan: 12,
                    },
                    {
                        name: "webSite",
                        label: "Website",
                        type: "input",
                        pattern: "Website",
                        validation: [{ max: 255 }],
                        colSpan: 12,
                    },
                ],
            },
            {
                key: "address-info",
                title: "Address Information",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: 'countryId',
                        label: t('Country'),
                        type: 'select',
                        colSpan: 12, // Full width
                        placeholder: "Select country",
                        required: true,
                        requiredMessage: t("Country is required."),
                        showSearch: true, // Enable search functionality
                        options: [], // Will be filled dynamically
                        defaultValue: 104, // <-- Set Iraq as default
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { "dropdowns": ["country"] },
                            transformData: `(apiResponse) => apiResponse.country.map(item => ({ label: ${isArabic ? 'item.NameAr' : 'item.Name'}, value: item.Id }))`,

                        },
                    },
                    {
                        name: 'cityId',
                        label: 'City',
                        type: 'select',
                        required: true,
                        requiredMessage: 'City is required.',
                        showSearch: true, // Enable search functionality
                        colSpan: 12,
                        options: [],
                        dynamicOptions: {
                            url: "/City/SearchByCountryId",
                            method: 'GET',
                            transformData: `(apiResponse) => apiResponse.map(item => ({ label: ${isArabic ? 'item.nameAr' : 'item.name'}, value: item.id }))`,

                            dependsOn: ['countryId'],
                        },
                        disabledWhen: [
                            {
                                field: 'countryId',
                                operator: 'isFalsy',
                            },
                        ],
                    }, 
                    {
                        name: "area",
                        label: "Area",
                        type: "input",
                        pattern: "Area",
                        validation: [{ max: 100 }],
                        colSpan: 12,
                    },
                    {
                        name: "streetNo",
                        label: "Street No",
                        type: "input",
                        pattern: "StreetNo",
                        validation: [{ max: 50 }],
                        colSpan: 12,
                    },
                    {
                        name: "address",
                        label: "Address",
                        type: "textarea",
                        pattern: "Address",
                        validation: [{ max: 250 }],
                        colSpan: 24,
                    },
                ],
            },

            {
                key: "logo-info",
                title: "Logo",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: "logoFile",
                        label: "Upload Logo",
                        type: "fileUpload",
                        accept: "image/*",
                        uploadUrl: "/customer/upload-customer-logo", // change if needed
                        fileKey: "customerLogoFile",
                        responseValueKey: "data.path",
                        maxCount: 1,
                        colSpan: 12,
                        updateFieldName: 'logo',
                    },
                    {
                        name: 'logo',
                        label: 'Customer Logo',
                        type: 'input',
                        hidden: true,
                        colSpan: 12,
                            },
                ],
            },
        ],
    };



    return (
        <DynamicForm
            schema={customerFormSchema}
            submitApiUrl={API_ENDPOINTS.CUSTOMERS.SAVE_CUSTOMER}
            getByIdApiUrl={API_ENDPOINTS.CUSTOMERS.GET_CUSTOMER_BY_ID}
            updateApiUrl={API_ENDPOINTS.CUSTOMERS.UPDATE_CUSTOMER}
            refreshTableDataKey={QUERY_KEYS.CUSTOMERS.CUSTOMER}
        />
    );
}
